import React, { useState, useEffect } from "react";

import { FilterIcon as Filter } from "@packages/ds";
import PropTypes from "prop-types";
import InfiniteScroll from "react-infinite-scroller";
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import { useTheme } from "styled-components";

import NewProjectButton from "Components/NewProjectButton";
import * as Dropdown from "ds/Dropdown";
import PageLoading from "ds/PageLoading";
import {
  organizationByDescriptionIdSelector,
  organizationsSelector
} from "Reducers/organization/organization";
import {
  loadSubscriptions,
  loadingListSelector,
  selectOrganizationSubscriptions,
  subscriptionsPlanFilterOptionsSelector,
  loadNextSubscriptionsPage,
  subscriptionLinksManagerSelector,
  loadingNextListSelector
} from "Reducers/organization/subscription";
import TrialRemaining from "src/organization/pages/billing/plan/list/TrialRemaining";
import Illustration from "Vendor_resources/images/empty-projects.svg";
import IllustrationDark from "Vendor_resources/images/empty-projects_dark.svg";

import PlanDetails from "../PlanDetails";

import * as S from "./List.styles";

const PlanList = ({ organizationId }) => {
  const [areFiltersOpen, setAreFiltersOpen] = useState(false);
  const [activeFilters, updateActiveFilters] = useState([]);
  const [filterTitle, setFilterTitle] = useState("");
  const [openPreview, setOpenPreview] = useState();
  const [estimate, setEstimate] = useState();

  const dispatch = useDispatch();
  const intl = useIntl();
  const theme = useTheme();
  const subscriptionsLoading = useSelector(loadingListSelector);
  const subscriptions = useSelector(state =>
    selectOrganizationSubscriptions(state, { organizationId })
  );

  const planFilterOptions = useSelector(state =>
    subscriptionsPlanFilterOptionsSelector(state, { organizationId })
  );

  const subscriptionLinkManager = useSelector(state =>
    subscriptionLinksManagerSelector(state, { organizationId })
  );

  useEffect(() => {
    updateActiveFilters(planFilterOptions);
  }, [subscriptions?.length]);

  const organizations = useSelector(organizationsSelector);
  const organization = useSelector(state =>
    organizationByDescriptionIdSelector(state, {
      organizationDescriptionId: organizationId
    })
  );

  useEffect(() => {
    if (organization) {
      dispatch(loadSubscriptions({ organizationId }));
    }
  }, [organizationId, organization]);

  const loadEstimate = async subscriptionId => {
    setEstimate(undefined);

    if (!subscriptions) {
      return false;
    }

    const subscription = subscriptions?.find(s => s.id === subscriptionId);

    if (!subscription) {
      return false;
    }

    setEstimate(await subscription.getEstimate({ format: "complex" }));
  };

  const selectFilter = ({ value, label }) => {
    const filterExists = activeFilters.some(f => f.value === value);
    if (value === "all_type") {
      // Toggle all available filters on and off when "All type" is toggled
      filterExists
        ? updateActiveFilters([])
        : updateActiveFilters(planFilterOptions);
      return;
    }
    const currentFilter = activeFilters.filter(f => f.value !== "all_type");
    const allFiltersChecked =
      !filterExists && currentFilter.length === planFilterOptions.length - 2;
    if (allFiltersChecked) {
      // All filters are checked except "All type", lets ensure all type is also marked as
      // selected
      updateActiveFilters(planFilterOptions);
      return;
    }
    if (filterExists) {
      updateActiveFilters(currentFilter.filter(f => f.value !== value));
      return;
    }
    updateActiveFilters([...currentFilter, { value, label }]);
  };

  const subscriptionsFilter = item => {
    if (activeFilters.length === 0) return false;
    if (activeFilters.length === planFilterOptions.length) {
      return true;
    }
    return activeFilters.some(
      f => f.value.toLowerCase() === item.plan?.toLowerCase()
    );
  };

  const filterTitleFilter = item => {
    if (!filterTitle) {
      return true;
    }
    return item.project_title
      ?.toLowerCase()
      .includes(filterTitle?.toLowerCase());
  };

  const expand = (subscriptionId, projectId) => {
    if (projectId === openPreview) {
      setOpenPreview();
    } else {
      setOpenPreview(projectId);
      loadEstimate(subscriptionId);
    }
  };

  const subscriptionsNextLoading = useSelector(loadingNextListSelector);

  const loadMore = () => {
    dispatch(loadNextSubscriptionsPage({ organizationId }));
  };

  if (subscriptionsLoading) return <PageLoading iconOnly />;
  return (
    <S.Layout>
      {subscriptions?.length > 0 ? (
        <InfiniteScroll
          threshold={400}
          pageStart={0}
          loadMore={loadMore}
          hasMore={
            !subscriptionsNextLoading &&
            !subscriptionsLoading &&
            subscriptionLinkManager?.hasMore()
          }
          loader={<S.Loading />}
          initialLoad={false}
        >
          <S.PlansHeaderWrapper>
            <S.Title tag="h2">{intl.formatMessage({ id: "plans" })}</S.Title>

            <S.FiltersDiv>
              <S.SearchInput
                id="billing-plan-search"
                placeholder="Search"
                value={filterTitle}
                onBlurState
                closedByDefault
                onChange={value => setFilterTitle(value)}
              />
              <Dropdown.Root
                open={areFiltersOpen}
                onOpenChange={setAreFiltersOpen}
                id="billing_plan"
              >
                <Dropdown.Trigger>
                  <S.IconFilterWrapper aria-label="filter-plans">
                    <Filter />
                  </S.IconFilterWrapper>
                </Dropdown.Trigger>
                <Dropdown.Portal>
                  <Dropdown.Content align="end">
                    <S.FragmentLabel isMainLabel={true}>
                      <S.FilterDropdownLabel>
                        {intl.formatMessage({
                          id: "environmentTree.navbar.filter.label",
                          defaultMessage: "Filter by"
                        })}
                        <S.CloseWindow
                          onClick={() => setAreFiltersOpen(false)}
                        />
                      </S.FilterDropdownLabel>
                    </S.FragmentLabel>

                    {planFilterOptions.map(option => {
                      const isChecked = activeFilters.filter(
                        af => af.value === option.value
                      ).length;
                      return (
                        <Dropdown.CheckboxItem
                          key={option.value}
                          checked={isChecked}
                          onCheckedChanged={() =>
                            selectFilter({
                              value: option.value,
                              label: option.label
                            })
                          }
                          id={option.value}
                          disabled={false}
                        >
                          {option.label}
                        </Dropdown.CheckboxItem>
                      );
                    })}
                  </Dropdown.Content>
                </Dropdown.Portal>
              </Dropdown.Root>
            </S.FiltersDiv>
          </S.PlansHeaderWrapper>
          <TrialRemaining />

          {subscriptions
            ?.filter(subscriptionsFilter)
            .filter(filterTitleFilter)
            .map((sub, index) => (
              <PlanDetails
                isOpen={openPreview === sub.project_id}
                onOpen={() => expand(sub.id, sub.project_id)}
                key={index}
                organizationId={organizationId}
                organizations={organizations}
                subscription={sub}
                estimate={estimate}
              />
            ))}
        </InfiniteScroll>
      ) : (
        <S.EmptyWrapper>
          <S.EmptyImage>
            <img
              src={theme.name === "dark" ? IllustrationDark : Illustration}
              alt={intl.formatMessage({ id: "billing.plan.list.empty.title" })}
            />
          </S.EmptyImage>
          <S.NoProjectPlansTitle tag="h2">
            {intl.formatMessage({
              id: "billing.plan.list.empty.title",
              defaultMessage: "You have no project plans"
            })}
          </S.NoProjectPlansTitle>
          <p>
            {intl.formatMessage({
              id: "billing.plan.list.empty.description",
              defaultMessage:
                "Find plan information here for any projects that you own."
            })}
          </p>
          <NewProjectButton
            organizationId={organizationId}
            isLink
            linkVariant="link"
          />
        </S.EmptyWrapper>
      )}
    </S.Layout>
  );
};

PlanList.propTypes = {
  organizationId: PropTypes.string
};

export default PlanList;
